// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import './v1.css';
import { DEFAULT_COMPONENT_ICONS, FluentThemeProvider } from '@azure/communication-react';
import { initializeIcons, registerIcons } from '@fluentui/react';
import { PrimaryButton, Stack, Text } from '@fluentui/react';
import { useEffect, useState } from 'react';
import { OpenAiView } from '../views/OpenAiView';

initializeIcons();
registerIcons({ icons: DEFAULT_COMPONENT_ICONS });
const redirectUri = encodeURIComponent('/app/v1');

function App(): JSX.Element {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);

  const startSSO = async (): Promise<void> => {
    window.location.href = `/api/sso/start?redirectUri=${redirectUri}`;
  };

  const logout = async (): Promise<void> => {
    window.location.href = '/api/sso/logout';
  };

  useEffect(() => {
    const checkLoginStatus = async (): Promise<void> => {
      try {
        const response = await fetch('/api/sso/status', { credentials: 'include' });
        const data: { isAuthenticated: boolean; user?: { username?: string; name?: string } | null } =
          await response.json();
        setIsAuthenticated(!!data.isAuthenticated);
        if (data.user?.username) {
          setUserEmail(data.user.username);
        } else {
          setUserEmail('');
        }
        if (data.user?.name) {
          setUserDisplayName(data.user.name);
        } else if (data.user?.username) {
          setUserDisplayName(data.user.username);
        } else {
          setUserDisplayName('');
        }
      } catch (error) {
        setAuthError('Unable to verify login status. Please try again.');
        setIsAuthenticated(false);
        setUserEmail('');
        setUserDisplayName('');
      } finally {
        setHasCheckedStatus(true);
      }
    };

    checkLoginStatus();
  }, []);

  useEffect(() => {
    if (hasCheckedStatus && !isAuthenticated) {
      startSSO();
    }
  }, [hasCheckedStatus, isAuthenticated]);

  useEffect(() => {
    const handler = (): void => {
      setAuthError('Session expired or unauthorized. Please log in again.');
      setIsAuthenticated(false);
      setUserEmail('');
      setUserDisplayName('');
    };
    window.addEventListener('auth-unauthorized', handler);
    return () => window.removeEventListener('auth-unauthorized', handler);
  }, []);

  return (
    <FluentThemeProvider>
      <Stack
        tokens={{ childrenGap: 32 }}
        styles={{
          root: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Stack tokens={{ childrenGap: 32 }} styles={{ root: { flexGrow: 1 } }}>
          {authError && (
            <Stack tokens={{ childrenGap: 12 }}>
              <Text variant="medium">{authError}</Text>
              <PrimaryButton onClick={startSSO}>Log in</PrimaryButton>
            </Stack>
          )}
          {isAuthenticated && (
            <OpenAiView
              userName={userDisplayName || userEmail || undefined}
              userEmail={userEmail || undefined}
              onLogout={logout}
            />
          )}
        </Stack>
      </Stack>
    </FluentThemeProvider>
  );
}

export default App;
